// One connected Shopify store. Rendered by SalesChannelsAdmin for every
// channel whose vendorFamily is "shopify" — there can be any number.
//
// Deliberately a separate component from ChannelCard rather than another
// branch inside it: almost nothing a store needs is what a partner
// channel needs. There is no base URL to type (the domain determines it),
// no HMAC pair to mint (Shopify issues the token and the signing secret),
// and there IS a setup sequence — credentials, then catalogue, then
// locations, then webhooks — that a partner channel has no equivalent of.
//
// The card is ordered as that sequence, top to bottom, because the steps
// genuinely depend on each other: you cannot list locations without a
// working token, and stock will not move without both a catalogue and a
// location mapping.

import { useCallback, useEffect, useState } from "react";
import {
    Loader2, Wifi, WifiOff, RefreshCw, Trash2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import SecretField from "./SecretField";
import VendorSubAccountRow from "./VendorSubAccountRow";
import SkuMapPanel from "./SkuMapPanel";
import type {
    ChannelPatch, SalesChannelConfig, ShopifyLocation, ShopifyWebhookTopic,
} from "./types";

interface Props {
    channel: SalesChannelConfig;
    onUpdated: (next: SalesChannelConfig) => void;
    onDeleted: (channelCode: string) => void;
}

interface WarehouseOption {
    warehouse: string;
    country: string | null;
}

const fmtAbsolute = (iso: string | null | undefined): string => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ShopifyStoreCard = ({ channel, onUpdated, onDeleted }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const base = `/admin/integrations/${encodeURIComponent(channel.channelCode)}`;

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<
        { ok: boolean; error: string | null; latencyMs: number; shop: { name?: string } | null } | null
    >(null);

    const [syncing, setSyncing] = useState(false);
    // Bumped after a catalogue sync so the SKU-map panel reloads.
    const [skuReload, setSkuReload] = useState(0);
    const [locations, setLocations] = useState<ShopifyLocation[] | null>(null);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const [webhooks, setWebhooks] = useState<ShopifyWebhookTopic[] | null>(null);
    const [publicBaseUrl, setPublicBaseUrl] = useState<string>("");
    const [registeringHooks, setRegisteringHooks] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [apiVersionDraft, setApiVersionDraft] = useState(channel.apiVersion);
    const [displayNameDraft, setDisplayNameDraft] = useState(channel.displayName);
    useEffect(() => { setApiVersionDraft(channel.apiVersion); }, [channel.id, channel.apiVersion]);
    useEffect(() => { setDisplayNameDraft(channel.displayName); }, [channel.id, channel.displayName]);

    // Warehouse list drives both the vendor routing rows and the
    // location-mapping table.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch("/settings", {});
                if (!res.ok) return;
                const rows = await res.json();
                if (cancelled) return;
                setWarehouses(
                    (Array.isArray(rows) ? rows : []).map((r) => ({
                        warehouse: r.warehouse,
                        country: r.country ?? null,
                    })),
                );
            } catch { /* silent — the rows just show no options */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const patch = useCallback(async (body: ChannelPatch): Promise<void> => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const res = await apiFetch(base, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) onUpdated(json.channel);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setError(msg);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [base, onUpdated, readOnly]);

    const toggle = (field: keyof ChannelPatch, value: boolean) => {
        patch({ [field]: value } as ChannelPatch).catch(() => { /* surfaced */ });
    };

    const runTest = async () => {
        if (readOnly) return;
        setTesting(true);
        setTestResult(null);
        try {
            const res = await apiFetch(`${base}/shopify/test`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            setTestResult({
                ok: !!json?.ok,
                error: json?.error ?? json?.message ?? null,
                latencyMs: json?.latencyMs ?? 0,
                shop: json?.shop ?? null,
            });
        } catch (err) {
            setTestResult({
                ok: false,
                error: err instanceof Error ? err.message : String(err),
                latencyMs: 0,
                shop: null,
            });
        } finally {
            setTesting(false);
        }
    };

    const syncCatalogue = async () => {
        if (readOnly) return;
        setSyncing(true);
        setError(null);
        try {
            const res = await apiFetch(`${base}/shopify/catalogue-sync`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) onUpdated(json.channel);
            toast.success(t("shopify_catalogue_synced", {
                mapped: String(json?.mapped ?? 0),
                matched: String(json?.matched ?? 0),
            }));
            setSkuReload((n) => n + 1);
            if (Array.isArray(json?.duplicates) && json.duplicates.length > 0) {
                // Two variants sharing a SKU means only one of them can
                // ever receive stock — worth saying out loud.
                toast.warn(t("shopify_catalogue_duplicates", {
                    skus: json.duplicates.slice(0, 5).join(", "),
                }));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSyncing(false);
        }
    };

    const loadLocations = async () => {
        setLoadingLocations(true);
        setError(null);
        try {
            const res = await apiFetch(`${base}/shopify/locations`);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            setLocations(Array.isArray(json?.locations) ? json.locations : []);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingLocations(false);
        }
    };

    const setLocationFor = async (warehouse: string, locationGid: string) => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const chosen = (locations ?? []).find((l) => l.gid === locationGid);
            const res = await apiFetch(`${base}/shopify/locations`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    warehouse,
                    locationGid: locationGid || null,
                    locationName: chosen?.name ?? null,
                }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            if (json?.channel) onUpdated(json.channel);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const loadWebhooks = async () => {
        try {
            const res = await apiFetch(`${base}/shopify/webhooks`);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            setWebhooks(Array.isArray(json?.topics) ? json.topics : []);
            setPublicBaseUrl(json?.publicBaseUrl ?? "");
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    };

    const registerWebhooks = async () => {
        if (readOnly) return;
        setRegisteringHooks(true);
        setError(null);
        try {
            const res = await apiFetch(`${base}/shopify/webhooks`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || json?.ok === false) {
                throw new Error(
                    Array.isArray(json?.errors) && json.errors.length > 0
                        ? json.errors.join("; ")
                        : json?.message ?? `HTTP ${res.status}`,
                );
            }
            const created = (json?.created?.length ?? 0) + (json?.replaced?.length ?? 0);
            toast.success(t("shopify_webhooks_registered", { n: String(created) }));
            await loadWebhooks();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setRegisteringHooks(false);
        }
    };

    const doDelete = async () => {
        if (readOnly) return;
        setDeleting(true);
        setError(null);
        try {
            const res = await apiFetch(base, { method: "DELETE" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
            toast.success(t("shopify_store_deleted", { name: channel.displayName }));
            onDeleted(channel.channelCode);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setConfirmDelete(false);
        } finally {
            setDeleting(false);
        }
    };

    const vendors = channel.vendors ?? [];
    const mappedWarehouses = new Set(channel.locations.map((l) => l.warehouse));
    // Every warehouse any vendor on this store fulfils from. Those are
    // exactly the ones that need a Shopify location, so the mapping table
    // lists them rather than all 13 sites.
    const routedWarehouses = [
        ...new Set(vendors.flatMap((v) => v.fulfilmentWarehouses ?? [])),
    ].sort();
    const unmappedRouted = routedWarehouses.filter((w) => !mappedWarehouses.has(w));
    const anyWrongEnvironment = (webhooks ?? []).some((w) => w.wrongEnvironmentUrl);
    const inputsDisabled = saving || deleting || readOnly;

    return (
        <details className="admin-integrations-card-details" id={channel.channelCode}>
            <summary className="admin-integrations-card-summary">
                <span className="admin-integrations-card-summary-title">
                    {channel.displayName}
                </span>
                <span className="admin-integrations-card-summary-sub">
                    {channel.shopDomain}
                </span>
                {channel.enabled && channel.pushMode === "dry_run" && (
                    <span className="admin-integrations-pill admin-integrations-pill--dry-run">
                        {t("integrations_push_mode_dry_run")}
                    </span>
                )}
                <span
                    className={
                        channel.enabled
                            ? "admin-integrations-status-pill admin-integrations-status-pill--ok"
                            : "admin-integrations-status-pill admin-integrations-status-pill--off"
                    }
                >
                    {channel.enabled
                        ? t("integrations_status_pill_enabled")
                        : t("integrations_status_pill_disabled")}
                </span>
            </summary>

            <div className="admin-integrations-card-body">
                <p className="admin-integrations-card-hint">{t("shopify_store_hint")}</p>

                <div className="admin-integrations-form">
                    {/* --- Step 1: on/off + how hard it pushes ------------ */}
                    <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                        <label className="admin-settings-toggle" title={t("integrations_master_enabled_hint")}>
                            <input
                                type="checkbox"
                                checked={channel.enabled}
                                onChange={() => toggle("enabled", !channel.enabled)}
                                disabled={inputsDisabled}
                            />
                            <span className="admin-settings-slider" />
                        </label>
                        <span className="admin-integrations-toggle-label">
                            {t("integrations_master_enabled")}
                        </span>
                        {saving && <Loader2 size={14} className="admin-integrations-spinner" />}
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("integrations_push_mode")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_push_mode_hint")}</p>
                        <div className="admin-integrations-mode-toggle">
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn${channel.pushMode === "dry_run" ? " admin-integrations-mode-btn--active" : ""}`}
                                onClick={() => { if (channel.pushMode !== "dry_run") patch({ pushMode: "dry_run" }).catch(() => {}); }}
                                disabled={inputsDisabled}
                            >
                                {t("integrations_push_mode_dry_run")}
                            </button>
                            <button
                                type="button"
                                className={`admin-integrations-mode-btn admin-integrations-mode-btn--live${channel.pushMode === "live" ? " admin-integrations-mode-btn--active" : ""}`}
                                onClick={() => { if (channel.pushMode !== "live") patch({ pushMode: "live" }).catch(() => {}); }}
                                disabled={inputsDisabled}
                            >
                                {t("integrations_push_mode_live")}
                            </button>
                        </div>
                    </div>

                    {/* --- Step 2: identity + credentials ----------------- */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_field_display_name")}
                        </label>
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={displayNameDraft}
                            onChange={(e) => setDisplayNameDraft(e.target.value)}
                            onBlur={() => {
                                const v = displayNameDraft.trim();
                                if (v && v !== channel.displayName) patch({ displayName: v }).catch(() => {});
                            }}
                            disabled={inputsDisabled}
                        />
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_field_domain")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_field_domain_hint")}</p>
                        <input
                            type="text"
                            className="admin-integrations-input"
                            value={channel.shopDomain}
                            readOnly
                            disabled
                        />
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_field_api_version")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_field_api_version_hint")}</p>
                        <input
                            type="text"
                            className="admin-integrations-input admin-integrations-input--time"
                            value={apiVersionDraft}
                            placeholder="2026-07"
                            onChange={(e) => setApiVersionDraft(e.target.value)}
                            onBlur={() => {
                                const v = apiVersionDraft.trim();
                                if (v !== channel.apiVersion) patch({ apiVersion: v || null }).catch(() => {});
                            }}
                            disabled={inputsDisabled}
                        />
                    </div>

                    <SecretField
                        label={t("shopify_field_access_token")}
                        hint={t("shopify_field_access_token_hint")}
                        secret={channel.outboundSecret}
                        onSave={(v) => patch({ outboundSecret: v })}
                        onClear={() => patch({ outboundSecret: null })}
                        disabled={readOnly}
                    />

                    <SecretField
                        label={t("shopify_field_webhook_secret")}
                        hint={t("shopify_field_webhook_secret_hint")}
                        secret={channel.inboundSecret}
                        onSave={(v) => patch({ inboundSecret: v })}
                        onClear={() => patch({ inboundSecret: null })}
                        disabled={readOnly}
                    />

                    <div className="admin-integrations-test-row">
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={runTest}
                            disabled={testing || !channel.outboundSecret.set || readOnly}
                        >
                            {testing ? (
                                <><Loader2 size={14} className="admin-integrations-spinner" /> {t("integrations_test_running")}</>
                            ) : (
                                <><Wifi size={14} /> {t("integrations_test_connection")}</>
                            )}
                        </button>
                        {testResult && (
                            <span
                                className={
                                    testResult.ok
                                        ? "admin-integrations-test-result admin-integrations-test-result--ok"
                                        : "admin-integrations-test-result admin-integrations-test-result--fail"
                                }
                            >
                                {testResult.ok ? (
                                    t("shopify_test_success", {
                                        shop: testResult.shop?.name ?? channel.shopDomain,
                                        latencyMs: String(testResult.latencyMs),
                                    })
                                ) : (
                                    <><WifiOff size={14} /> {t("integrations_test_failure", { reason: testResult.error ?? "?" })}</>
                                )}
                            </span>
                        )}
                    </div>

                    {/* --- Step 3: order behaviour ------------------------ */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_require_paid")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_require_paid_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={channel.shopifyRequirePaid}
                                    onChange={() => toggle("shopifyRequirePaid", !channel.shopifyRequirePaid)}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.shopifyRequirePaid
                                    ? t("shopify_require_paid_on")
                                    : t("shopify_require_paid_off")}
                            </span>
                        </div>
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_notify_customer")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_notify_customer_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={channel.shopifyNotifyCustomer}
                                    onChange={() => toggle("shopifyNotifyCustomer", !channel.shopifyNotifyCustomer)}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.shopifyNotifyCustomer
                                    ? t("shopify_notify_customer_on")
                                    : t("shopify_notify_customer_off")}
                            </span>
                        </div>
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("integrations_consolidate_orders")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("integrations_consolidate_orders_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={channel.consolidateOrders}
                                    onChange={() => toggle("consolidateOrders", !channel.consolidateOrders)}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.consolidateOrders
                                    ? t("integrations_consolidate_orders_on")
                                    : t("integrations_consolidate_orders_off")}
                            </span>
                        </div>
                    </div>

                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("integrations_enforce_extra_protection")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("integrations_enforce_extra_protection_hint")}</p>
                        <div className="admin-integrations-form-row admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={channel.enforceExtraProtection}
                                    onChange={() => toggle("enforceExtraProtection", !channel.enforceExtraProtection)}
                                    disabled={inputsDisabled}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                            <span className="admin-integrations-toggle-label">
                                {channel.enforceExtraProtection
                                    ? t("integrations_enforce_extra_protection_on")
                                    : t("integrations_enforce_extra_protection_off")}
                            </span>
                        </div>
                    </div>

                    {/* --- Step 4: catalogue ------------------------------ */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_catalogue")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_catalogue_hint")}</p>
                        <div className="admin-integrations-test-row">
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={syncCatalogue}
                                disabled={syncing || !channel.outboundSecret.set || readOnly}
                            >
                                {syncing ? (
                                    <><Loader2 size={14} className="admin-integrations-spinner" /> {t("shopify_catalogue_syncing")}</>
                                ) : (
                                    <><RefreshCw size={14} /> {t("shopify_catalogue_sync")}</>
                                )}
                            </button>
                            <span className="admin-integrations-status-value">
                                {channel.catalogueSyncedAt
                                    ? t("shopify_catalogue_state", {
                                        n: String(channel.catalogueSkuCount ?? 0),
                                        when: fmtAbsolute(channel.catalogueSyncedAt),
                                    })
                                    : t("shopify_catalogue_never")}
                            </span>
                        </div>
                    </div>

                    {/* --- Step 5: warehouse routing + location mapping --- */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_locations")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_locations_hint")}</p>

                        {routedWarehouses.length === 0 ? (
                            <p className="admin-integrations-vendors-empty">
                                {t("shopify_locations_no_routing")}
                            </p>
                        ) : (
                            <>
                                <div className="admin-integrations-test-row">
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        onClick={loadLocations}
                                        disabled={loadingLocations || !channel.outboundSecret.set}
                                    >
                                        {loadingLocations ? (
                                            <><Loader2 size={14} className="admin-integrations-spinner" /> {t("loading")}</>
                                        ) : (
                                            <><RefreshCw size={14} /> {t("shopify_locations_load")}</>
                                        )}
                                    </button>
                                    {unmappedRouted.length > 0 && (
                                        <span className="admin-integrations-test-result admin-integrations-test-result--fail">
                                            <AlertTriangle size={14} />{" "}
                                            {t("shopify_locations_unmapped", {
                                                warehouses: unmappedRouted.join(", "),
                                            })}
                                        </span>
                                    )}
                                </div>
                                <div className="admin-integrations-vendors-list">
                                    {routedWarehouses.map((w) => {
                                        const current = channel.locations.find((l) => l.warehouse === w);
                                        return (
                                            <div key={w} className="admin-integrations-form-row">
                                                <span className="admin-integrations-inline-label">{w}</span>
                                                <select
                                                    className="admin-integrations-input"
                                                    value={current?.locationGid ?? ""}
                                                    onChange={(e) => setLocationFor(w, e.target.value)}
                                                    disabled={inputsDisabled || locations === null}
                                                >
                                                    <option value="">{t("shopify_location_none")}</option>
                                                    {/* Keep the saved value selectable even before
                                                        the live list has been fetched, so opening the
                                                        card never looks like the mapping was lost. */}
                                                    {locations === null && current && (
                                                        <option value={current.locationGid}>
                                                            {current.locationName ?? current.locationGid}
                                                        </option>
                                                    )}
                                                    {(locations ?? []).map((l) => (
                                                        <option key={l.gid} value={l.gid}>
                                                            {l.name}{l.city ? ` — ${l.city}` : ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>

                    {/* --- Step 6: webhooks ------------------------------- */}
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">
                            {t("shopify_webhooks")}
                        </label>
                        <p className="admin-integrations-field-hint">{t("shopify_webhooks_hint")}</p>
                        <div className="admin-integrations-test-row">
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={loadWebhooks}
                                disabled={!channel.outboundSecret.set}
                            >
                                <RefreshCw size={14} /> {t("shopify_webhooks_check")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={registerWebhooks}
                                disabled={registeringHooks || !channel.outboundSecret.set || readOnly}
                            >
                                {registeringHooks ? (
                                    <><Loader2 size={14} className="admin-integrations-spinner" /> {t("shopify_webhooks_registering")}</>
                                ) : (
                                    t("shopify_webhooks_register")
                                )}
                            </button>
                        </div>
                        {publicBaseUrl && (
                            <p className="admin-integrations-field-hint">
                                {t("shopify_webhooks_target", { url: publicBaseUrl })}
                            </p>
                        )}
                        {/* The silent-cutover trap: a store still delivering to
                            another Portal environment looks like nothing at all
                            is wrong — no error, just no orders. */}
                        {anyWrongEnvironment && (
                            <p className="admin-integrations-field-error">
                                <AlertTriangle size={14} /> {t("shopify_webhooks_wrong_env")}
                            </p>
                        )}
                        {webhooks && (
                            <ul className="admin-integrations-push-job-errors-list">
                                {webhooks.map((w) => (
                                    <li key={w.topic}>
                                        {w.registered
                                            ? <CheckCircle2 size={14} />
                                            : <AlertTriangle size={14} />}{" "}
                                        <strong>{w.topic}</strong>
                                        {w.registered
                                            ? ` — ${t("shopify_webhook_ok")}`
                                            : w.wrongEnvironmentUrl
                                                ? ` — ${t("shopify_webhook_wrong_env_detail", { url: w.wrongEnvironmentUrl })}`
                                                : ` — ${t("shopify_webhook_missing")}`}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {error && <p className="admin-integrations-field-error">{error}</p>}
                </div>

                <SkuMapPanel channelCode={channel.channelCode} reloadToken={skuReload} />

                {/* Vendor sub-account — one per store, carrying the
                    fulfilment-warehouse priority list. Reused verbatim from
                    the partner cards: the routing question is identical. */}
                <details className="admin-integrations-vendors-details">
                    <summary className="admin-integrations-vendors-summary">
                        <span>{t("shopify_routing_heading")}</span>
                        <span className="admin-integrations-vendors-count">{vendors.length}</span>
                    </summary>
                    <div className="admin-integrations-vendors-body">
                        <p className="admin-integrations-vendors-hint">
                            {t("shopify_routing_hint")}
                        </p>
                        {vendors.length === 0 ? (
                            <p className="admin-integrations-vendors-empty">
                                {t("integrations_vendors_empty")}
                            </p>
                        ) : (
                            <div className="admin-integrations-vendors-list">
                                {vendors.map((v) => (
                                    <VendorSubAccountRow
                                        key={v.id}
                                        channelCode={channel.channelCode}
                                        vendor={v}
                                        warehouses={warehouses}
                                        onChannelUpdated={onUpdated}
                                        disabled={readOnly}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </details>

                {/* Destructive, and last. Refused server-side while any
                    order still references the store. */}
                <div className="admin-integrations-test-row">
                    {!confirmDelete ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => setConfirmDelete(true)}
                            disabled={!channel.isRemovable || inputsDisabled}
                            title={t("shopify_delete_store_hint")}
                        >
                            <Trash2 size={14} /> {t("shopify_delete_store")}
                        </button>
                    ) : (
                        <>
                            <span className="admin-integrations-secret-confirm-text">
                                {t("shopify_delete_store_confirm", { name: channel.displayName })}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={doDelete}
                                disabled={deleting || readOnly}
                            >
                                {deleting
                                    ? <Loader2 size={14} className="admin-integrations-spinner" />
                                    : t("shopify_delete_store")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => setConfirmDelete(false)}
                                disabled={deleting}
                            >
                                {t("cancel")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </details>
    );
};

export default ShopifyStoreCard;
